import { useState } from "react"
import { toast } from "sonner"
import { HugeiconsIcon } from "@hugeicons/react"
import { Copy01Icon, Tick01Icon } from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { GTM_TEMPLATE_NAME, GTM_GALLERY_URL } from "@/lib/utils/trackingSnippet"

type GtmInstructionsProps = {
  scriptId: string
  onIdCopied?: () => void
  className?: string
}

const GTM_STEPS = [
  <>In GTM open <strong>Tags → New → Tag Configuration → Discover more tag types</strong>.</>,
  <>Search <strong>"{GTM_TEMPLATE_NAME}"</strong> in the Community Template Gallery and add it.</>,
  <>Paste your <strong>Script ID</strong> (copy it above) into the tag field.</>,
  <>Set trigger to <strong>Initialization - All Pages</strong>, Save, then <strong>Submit</strong> to publish.</>,
]

export function GtmInstructions({ scriptId, onIdCopied, className }: GtmInstructionsProps) {
  const [idCopied, setIdCopied] = useState(false)

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(scriptId)
      setIdCopied(true)
      toast.success("Script ID copied")
      onIdCopied?.()
      setTimeout(() => setIdCopied(false), 2500)
    } catch {
      toast.error("Failed to copy Script ID")
    }
  }

  return (
    <div className={cn("space-y-3", className)}>
      {/* Script ID copy row */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-1.5">
          Your Script ID
        </p>
        <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/20 px-3 py-2">
          <code className="flex-1 font-mono text-[11px] text-foreground/90 select-all truncate">
            {scriptId}
          </code>
          <Button
            type="button"
            onClick={handleCopyId}
            variant={idCopied ? "outline" : "default"}
            size="sm"
            className="shrink-0 h-7 gap-1.5 px-2.5 text-[11px]"
          >
            {idCopied ? (
              <>
                <HugeiconsIcon icon={Tick01Icon} size={11} className="text-success dark:text-success" />
                Copied
              </>
            ) : (
              <>
                <HugeiconsIcon icon={Copy01Icon} size={11} />
                Copy ID
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Numbered steps */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-muted-foreground">
          Steps
        </p>
        {GTM_STEPS.map((step, index) => (
          <div key={index} className="flex items-start gap-2.5">
            <div className="shrink-0 w-4 h-4 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[11px] font-bold mt-0.5">
              {index + 1}
            </div>
            <p className="text-[11px] text-foreground/80 leading-snug">{step}</p>
          </div>
        ))}
      </div>

      {/* Gallery link + fallback note */}
      <div className="rounded-md border border-border/20 bg-muted/10 px-3 py-2 space-y-1">
        <a
          href={GTM_GALLERY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-primary hover:underline font-medium"
        >
          Browse template in GTM Gallery →
        </a>
        <p className="text-[11px] text-muted-foreground">
          Prefer raw code? Switch to <strong>Manual</strong> to copy the full script tag.
        </p>
      </div>
    </div>
  )
}
